import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'

import { EmptyState } from '@/components/EmptyState/EmptyState'
import { Input } from '@/components/ui/input'
import { useLazyDeletedSocialChecksQuery } from '@/store/api/Person/Person.query'
import { formatDate } from '@/utils/date'
import { DeletedSocialAnalysisItem } from './DeletedSocialAnalysisItem'
import type { IDeletedHistoryItem } from '@/store/api/Enterprise/Enterprise.types'

interface DeletedReportsTabProps {
  isActive: boolean
}

export const DeletedReportsTab = ({ isActive }: DeletedReportsTabProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const [fetchDeleted, { data: deletedData, isLoading, isFetching }] =
    useLazyDeletedSocialChecksQuery()

  useEffect(() => {
    if (isActive) {
      fetchDeleted()
    }
  }, [isActive, fetchDeleted])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  const reports = useMemo(() => deletedData?.info || [], [deletedData])

  const filteredDeletedReports = useMemo(() => {
    if (!search) return reports

    const searchLower = search.toLowerCase()
    return reports.filter((report: IDeletedHistoryItem) => {
      const name = report.subjectFullName?.toLowerCase() || ''
      const reason = report.deletionReason?.toLowerCase() || ''
      const date = formatDate(report.originalCompletedAt, 'MM/dd/yyyy') || ''

      return (
        name.includes(searchLower) ||
        reason.includes(searchLower) ||
        date.includes(searchLower)
      )
    })
  }, [reports, search])

  const isLoadingFirstTime = (isLoading || isFetching) && reports.length === 0

  if (reports.length === 0 && !isLoadingFirstTime) {
    return (
      <div className="flex flex-col align-middle items-center justify-center h-full bg-baseColor rounded-2xl p-10">
        <EmptyState
          text={
            search === ''
              ? t('dashboard.reports.deleted.emptyState')
              : `${t('dashboard.reports.noResultsFor')} "${search}"`
          }
        />
      </div>
    )
  }

  return (
    <div className={'flex flex-col gap-5 h-full max-sm:h-[75vh]'}>
      <div className="w-full flex justify-between sm:items-center max-sm:flex-col max-sm:gap-5">
        <Input
          placeholder={t('dashboard.reports.search')}
          className="flex h-[46px] w-[354.67px] rounded-lg max-sm:w-full"
          value={search}
          useDebounce={true}
          debounceTime={300}
          onChange={handleSearch}
        />
      </div>

      <div className="flex flex-col gap-4 custom-scrollbar overflow-y-auto h-full">
        {filteredDeletedReports.map((report, index) => (
          <DeletedSocialAnalysisItem {...report} key={index} />
        ))}
        {filteredDeletedReports.length === 0 && search !== '' && (
          <div className="flex justify-center h-full items-center">
            <p className="text-xl font-medium text-neutralColor80">
              {t('dashboard.reports.noResultsFor')} "{search}"
            </p>
          </div>
        )}
      </div>
      <div className="flex items-start justify-center gap-1">
        <Info size={16} className="text-neutralColor60" />
        <p className="text-xs font-normal text-neutralColor60 text-center ">
          {t('dashboard.reports.disclaimer')}
        </p>
      </div>
    </div>
  )
}
